import React from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Card, Flex, Grid, Popconfirm, Tag, Typography, theme } from 'antd';
import { ChargeBeePlanId } from '@prerender/billing-shared';
import {
  GSC_SETTINGS_ROUTE,
  SEARCH_PERFORMANCE_CONNECT_ROUTE,
  SEARCH_PERFORMANCE_PLAN_COPY,
  SEARCH_PERFORMANCE_RECONNECT_ROUTE,
  SearchPerformanceDomainCapCounter,
  SearchPerformanceUpgradeRow,
  isSearchPerformanceCapUpgradable,
  readGscConnectionState,
  useDisconnectGscMutation,
  useGetGscConnectionStatusQuery,
  useSearchPerformanceDomainCap,
  useSearchPerformancePlanLabel,
  useTrackSearchPerformanceEvent,
} from '~features/SearchPerformance';

import ButtonWithRole, { useIsDisabledForRole } from '~components/ButtonWithRole';
import { CustomTooltip } from '~components/Tooltip';
import { dates } from '~library/dates';
import { GSC_DISABLED_ROLES } from './GscIntegrationRoles';
import { SEARCH_PERFORMANCE_CARD_DESCRIPTION, SEARCH_PERFORMANCE_CARD_TITLE } from './SearchPerformanceCardCopy';

// Names the Search Performance origin (PRE-3271 #6) so the shared
// `UpgradeClicked` event tells this at-cap upsell apart from other Integrations CTAs.
const UPGRADE_CTA_LOCATION = 'Search Performance - At Cap';

const STRINGS = {
  title: SEARCH_PERFORMANCE_CARD_TITLE,
  description: SEARCH_PERFORMANCE_CARD_DESCRIPTION,
  sectionTitle: 'Connected Google account',
  connectFirst: 'Connect Google account',
  accountFallbackName: 'This Google account',
  // "GSC domains", not "domains": the card also carries the plan's domain
  // allowance, and an unqualified label read as if it managed that too.
  manageDomains: 'Manage GSC domains',
  actionLabel: (action: string, account: string) => `${action}: ${account}`,
  manageDomainsBlockedByBreak:
    'Reconnect this account first. Choosing domains needs a working connection to Google, so there is nothing to list until then.',
  reconnect: 'Reconnect',
  connected: 'Connected',
  reconnectNeeded: 'Reconnect needed',
  statusHelp: (lastSync: string | null) =>
    lastSync ? `Bringing in data. Last synced ${lastSync}.` : 'Bringing in data. The first sync is still running.',
  brokenStatusHelp: (lastSync: string | null) =>
    lastSync
      ? `Google stopped accepting the connection, so nothing has arrived since ${lastSync}. Reconnect to resume.`
      : 'Google stopped accepting the connection, so no data has arrived. Reconnect to resume.',
  accountSummary: (synced: number, lastSync: string | null) =>
    `${synced} ${synced === 1 ? 'property' : 'properties'} synced · ` +
    (lastSync ? `last synced ${lastSync}` : 'waiting for the first sync'),
  accessLost: (propertyUrls: string[], isLostOnly: boolean) => {
    const [subject, them, their, theyWere] =
      propertyUrls.length === 1
        ? [propertyUrls[0], 'it', 'its', 'it was']
        : [propertyUrls.join(', '), 'them', 'their', 'they were'];
    const fix = isLostOnly
      ? `Add ${them} back in Search Console and reconnect from Manage GSC domains, or disconnect the account.`
      : `Remove ${them} under Manage GSC domains, or add ${them} back in Search Console and re-add ${them} there.`;
    return `${subject} lost Search Console access — ${theyWere} removed from this Google account or ${their} permission was revoked. ${fix}`;
  },
  disconnect: 'Disconnect',
  confirmTitle: 'Disconnect this Google account?',
  // Not "synced" properties: the same modal opens over a broken connection,
  // whose properties stopped reporting days ago.
  confirmBodyLead: (accountName: string, properties: number) =>
    `${accountName} and its ${properties} ${
      properties === 1 ? 'property' : 'properties'
    } will stop reporting into Search Performance.`,
  confirmBodyTail: 'Search Performance will have no Google data until you connect an account again.',
  confirmOk: 'Yes, disconnect',
  confirmCancel: 'Keep it connected',
  domainCapHelpAtCap: 'You have used every domain in your plan. Remove one to sync a different domain.',
  disconnectSuccess: 'Google Search Console disconnected.',
  disconnectError: 'Could not disconnect Google Search Console. Please try again.',
};

/** The Search Performance integration card (PRE-3212 §2a). */
function SearchPerformanceCard() {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isNarrow = !screens.md;
  const navigate = useNavigate();
  const trackEvent = useTrackSearchPerformanceEvent();
  const { data: status, isLoading: isStatusLoading } = useGetGscConnectionStatusQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [disconnectGsc, { isLoading: isDisconnecting, isSuccess: isDisconnected }] = useDisconnectGscMutation();
  // Needed beyond ButtonWithRole: a disabled trigger still lets Popconfirm open
  // its bubble, offering a read-only role a confirm button that does nothing.
  const isReadOnlyRole = useIsDisabledForRole(GSC_DISABLED_ROLES);
  const domainCap = useSearchPerformanceDomainCap();
  const planLabel = useSearchPerformancePlanLabel();

  const connectedAccountEmail = status?.connectedAccountEmail ?? null;
  const { domains, liveDomains, lastSyncAt, isBroken, lostUrls } = readGscConnectionState(
    status?.sources ?? [],
    connectedAccountEmail
  );
  const accountHeading = connectedAccountEmail ?? domains.join(', ');
  const lastSyncLabel = lastSyncAt ? dates.pointInTimeWithAge(lastSyncAt) : null;

  const isConnected = !isDisconnected && domains.length > 0;
  const atCap = liveDomains.length >= domainCap;
  const showEnterpriseUpsell = isConnected && atCap && isSearchPerformanceCapUpgradable(domainCap);

  // Connect route, not the dashboard: a live partial connection would no-op there.
  const handleConnect = () => {
    trackEvent('Search Performance: Integration Connect Clicked');
    navigate(SEARCH_PERFORMANCE_CONNECT_ROUTE);
  };

  // `?reconnect=1` — without it a still-live source lands on the sync step.
  const handleReconnect = () => {
    trackEvent('Search Performance: Integration Reconnect Clicked');
    navigate(SEARCH_PERFORMANCE_RECONNECT_ROUTE);
  };

  const handleManageDomains = () => {
    trackEvent('Search Performance: Manage Domains Clicked');
    navigate(GSC_SETTINGS_ROUTE);
  };

  const handleDisconnect = async () => {
    try {
      await disconnectGsc().unwrap();
      trackEvent('Search Performance: Integration Disconnected', { method: 'manual' });
      message.success(STRINGS.disconnectSuccess);
    } catch {
      message.error(STRINGS.disconnectError);
    }
  };

  return (
    <Card
      title={STRINGS.title}
      loading={isStatusLoading}
      extra={
        <SearchPerformanceDomainCapCounter
          used={liveDomains.length}
          max={domainCap}
          planLabel={planLabel}
          helpAtCap={STRINGS.domainCapHelpAtCap}
        />
      }
      styles={{ body: { paddingTop: token.padding, paddingBottom: token.padding } }}
    >
      {!isConnected ? (
        <Flex vertical={isNarrow} justify="space-between" align={isNarrow ? 'stretch' : 'center'} gap={token.marginLG}>
          <Typography.Text>{STRINGS.description}</Typography.Text>
          <ButtonWithRole
            type="primary"
            disabledFor={GSC_DISABLED_ROLES}
            onClick={handleConnect}
            style={{ flexShrink: 0 }}
          >
            {STRINGS.connectFirst}
          </ButtonWithRole>
        </Flex>
      ) : (
        <Flex vertical gap={token.marginXXS}>
          <Typography.Text>{STRINGS.description}</Typography.Text>
          <Typography.Text type="secondary" strong style={{ fontSize: token.fontSizeSM }}>
            {STRINGS.sectionTitle}
          </Typography.Text>

          <Flex
            vertical={isNarrow}
            align={isNarrow ? 'stretch' : 'center'}
            gap={isNarrow ? token.marginSM : token.margin}
            // FIXME: no exact token — the prototype row is 14px 0.
            style={{ padding: `${token.paddingSM}px 0` }}
          >
            <Flex vertical flex={1} style={{ minWidth: 0 }}>
              <Flex align="center" gap={token.marginXS} wrap>
                <Typography.Text strong>{accountHeading}</Typography.Text>
                <CustomTooltip
                  placement="top"
                  message={isBroken ? STRINGS.brokenStatusHelp(lastSyncLabel) : STRINGS.statusHelp(lastSyncLabel)}
                >
                  <Tag color={isBroken ? 'warning' : 'success'} style={{ margin: 0 }}>
                    {isBroken ? STRINGS.reconnectNeeded : STRINGS.connected}
                  </Tag>
                </CustomTooltip>
              </Flex>
              <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {STRINGS.accountSummary(liveDomains.length, lastSyncLabel)}
              </Typography.Text>
              {!isBroken && lostUrls.length > 0 && (
                <Typography.Text type="warning" style={{ fontSize: token.fontSizeSM }}>
                  {STRINGS.accessLost(lostUrls, liveDomains.length === 0)}
                </Typography.Text>
              )}
            </Flex>

            <Flex gap={token.marginXS} style={{ flexShrink: 0 }} wrap>
              {isBroken && (
                <ButtonWithRole
                  type="primary"
                  disabledFor={GSC_DISABLED_ROLES}
                  aria-label={STRINGS.actionLabel(STRINGS.reconnect, accountHeading)}
                  onClick={handleReconnect}
                >
                  {STRINGS.reconnect}
                </ButtonWithRole>
              )}
              {/* Disabled rather than hidden: this is the only route to the list. */}
              <CustomTooltip placement="top" message={isBroken ? STRINGS.manageDomainsBlockedByBreak : undefined}>
                <ButtonWithRole
                  disabledFor={GSC_DISABLED_ROLES}
                  disabled={isBroken}
                  aria-label={STRINGS.actionLabel(STRINGS.manageDomains, accountHeading)}
                  onClick={handleManageDomains}
                >
                  {STRINGS.manageDomains}
                </ButtonWithRole>
              </CustomTooltip>
              <Popconfirm
                title={STRINGS.confirmTitle}
                description={
                  <Flex vertical gap={token.marginXXS}>
                    <span>
                      {STRINGS.confirmBodyLead(connectedAccountEmail ?? STRINGS.accountFallbackName, domains.length)}
                    </span>
                    <span>{STRINGS.confirmBodyTail}</span>
                  </Flex>
                }
                okText={STRINGS.confirmOk}
                cancelText={STRINGS.confirmCancel}
                okButtonProps={{ loading: isDisconnecting }}
                disabled={isReadOnlyRole}
                onConfirm={handleDisconnect}
              >
                <ButtonWithRole
                  danger
                  disabledFor={GSC_DISABLED_ROLES}
                  loading={isDisconnecting}
                  aria-label={STRINGS.actionLabel(STRINGS.disconnect, accountHeading)}
                >
                  {STRINGS.disconnect}
                </ButtonWithRole>
              </Popconfirm>
            </Flex>
          </Flex>

          {showEnterpriseUpsell && (
            <SearchPerformanceUpgradeRow
              message={SEARCH_PERFORMANCE_PLAN_COPY.proAtCap}
              ctaLabel={SEARCH_PERFORMANCE_PLAN_COPY.upgradeToEnterprise}
              targetPlanId={ChargeBeePlanId.EnterprisePlus}
              ctaLocation={UPGRADE_CTA_LOCATION}
            />
          )}
        </Flex>
      )}
    </Card>
  );
}

export default SearchPerformanceCard;
